<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { DragPreview, type IDraggingEntity } from "@vue-dnd-kit/core"
import { AnimatePresence, motion } from "motion-v"

defineOptions({
    name: "HomeWorkspaceDragPreview",
})

// ================================
// HELPERS
// ================================
/**
 * Échelle cible du fantôme par rapport à la carte source (ex. 0.5 = moitié).
 * Principal réglage pour la taille perçue pendant le drag.
 */
const DRAG_PREVIEW_BASE_SCALE = 0.5

/** Légère réduction à l’apparition / à la disparition (multiplicateur sur l’échelle finale). */
const DRAG_PREVIEW_MOTION_IN_OUT_FACTOR = 0.92

/** Largeur minimale de la prévisualisation (px) — évite une carte trop petite au drag. */
const MIN_DRAG_PREVIEW_WIDTH_PX = 220
/** Hauteur minimale de la prévisualisation (px). */
const MIN_DRAG_PREVIEW_HEIGHT_PX = 72

/**
 * Échelle du fantôme de drag : `DRAG_PREVIEW_BASE_SCALE` × taille d’origine, sans descendre sous les minimums
 * (plafonnée à 1 pour ne pas agrandir au-delà du bloc source).
 * @param rect - Boîte englobante de l’élément au début du drag
 */
function getDragPreviewScale(rect: DOMRect): number {
    const w = Math.max(rect.width, 1)
    const h = Math.max(rect.height, 1)
    let s = DRAG_PREVIEW_BASE_SCALE
    s = Math.max(
        s,
        MIN_DRAG_PREVIEW_WIDTH_PX / w,
        MIN_DRAG_PREVIEW_HEIGHT_PX / h
    )
    return Math.min(s, 1)
}

/**
 * Style de réduction de la preview : le `scale` sur `motion.div` ne suffit pas (Vue + téléport) ;
 * on applique `transform: scale(...)` ici — c’est ce qui lie `DRAG_PREVIEW_BASE_SCALE` à la taille réelle.
 * @param rect - Boîte source ; absent si données incomplètes
 */
function getDragPreviewTransformStyle(rect: DOMRect | undefined): {
    transform: string
    transformOrigin: string
} {
    const s = rect ? getDragPreviewScale(rect) : DRAG_PREVIEW_BASE_SCALE
    return {
        transform: `scale(${s}) rotate(1deg)`,
        transformOrigin: "center center",
    }
}

/**
 * Itère sur la carte des éléments en cours de drag (vue-dnd-kit).
 */
function getDraggingEntries(
    draggingMap: Map<HTMLElement, IDraggingEntity>
): [HTMLElement, IDraggingEntity][] {
    return Array.from(draggingMap.entries())
}
</script>

<template>
    <!--
      Prévisualisation animée (motion-v) — cf. Vue DnD Kit « Drag Animation ».
      https://zizigy.github.io/vue-dnd-kit/v2/examples/animation.html
    -->
    <AnimatePresence mode="popLayout">
        <DragPreview v-slot="{ draggingMap }">
            <motion.div
                v-for="[node, draggable] in getDraggingEntries(draggingMap)"
                :key="String(draggable.id ?? node)"
                class="pointer-events-none rounded-lg drop-shadow-2xl"
                :initial="{
                    scale: DRAG_PREVIEW_MOTION_IN_OUT_FACTOR,
                    opacity: 0,
                    rotate: -1,
                }"
                :animate="{
                    scale: 1,
                    opacity: 1,
                    rotate: 0,
                }"
                :exit="{
                    scale: DRAG_PREVIEW_MOTION_IN_OUT_FACTOR,
                    opacity: 0,
                    rotate: -1,
                }"
                :transition="{ type: 'spring', stiffness: 480, damping: 26 }"
            >
                <!--
                  Échelle métier : `getDragPreviewTransformStyle` (pas seulement width/minHeight du fallback HTML).
                  Les bâtiments passent par `draggable.render` — le bloc v-else ne s’affiche pas.
                -->
                <div
                    class="origin-center"
                    :style="getDragPreviewTransformStyle(draggable.initialRect)"
                >
                    <component v-if="draggable.render" :is="draggable.render" />
                    <div
                        v-else
                        v-html="draggable.initialOuterHTML"
                        :style="{
                            width: `${draggable.initialRect.width}px`,
                            minHeight: `${draggable.initialRect.height}px`,
                        }"
                    />
                </div>
            </motion.div>
        </DragPreview>
    </AnimatePresence>
</template>
